import React, { useMemo, useState } from "react";
import { Table, Modal, Button, Form, InputGroup } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { centimesToCurrency } from "helper/globalHelper";
import { updatePurchasePrice } from "state/adminData/actions";

/**
 * Récap de rentabilité (marge) du devis en cours d'édition.
 * Visible uniquement pour les admins, en mode briques.
 *
 * Coût d'achat :
 * - Produit : prix d'achat de la fiche.
 * - Groupe / forfait : prix saisi en priorité, sinon dérivé de la somme des prix
 *   d'achat des produits qui le composent (× quantité).
 *
 * Les offres / produits composants sans prix d'achat sont listés et cliquables :
 * une popup permet de renseigner le prix sans quitter l'éditeur. Le calcul est
 * fait côté client à partir du store, donc la mise à jour est immédiate.
 */
const isDeliveryBrique = (brique) =>
  (brique?.name || "").toLowerCase().includes("livraison");

const MarginSummaryPanel = ({
  briquesDevis = [],
  products = [],
  productGroups = [],
  productPackages = [],
  productParts = [],
  reduction = {},
  reductions = null,
}) => {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(null); // { type, id, name }
  const [draft, setDraft] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [detailDrafts, setDetailDrafts] = useState({}); // { "type-id": "valeur euros" }

  const { families, totals, missing, detail } = useMemo(() => {
    const productsById = {};
    (products || []).forEach((p) => { productsById[p.id] = p; });
    const groupsById = {};
    (productGroups || []).forEach((g) => { groupsById[g.id] = g; });
    const packagesById = {};
    (productPackages || []).forEach((p) => { packagesById[p.id] = p; });
    const partsById = {};
    (productParts || []).forEach((p) => { partsById[p.id] = p; });

    const productPurchase = (id) => {
      const p = productsById[id];
      if (!p) return null;
      const v = p.purchase_price_ht;
      return v === null || v === undefined ? null : Number(v);
    };

    // Coût d'achat unitaire d'une part = prix d'achat du produit parent / partsPerProduct
    const partPurchaseUnit = (partId) => {
      const part = partsById[partId];
      if (!part) return null;
      const parentPurchase = productPurchase(part.product_id);
      if (parentPurchase === null) return null;
      const ppp = Number(part.parts_per_product) || 1;
      return Math.round(parentPurchase / ppp);
    };

    // Cible éditable d'une part = son produit parent
    const partParentTarget = (partId) => {
      const part = partsById[partId];
      if (!part || !part.product_id) return null;
      const parent = productsById[part.product_id];
      return { id: part.product_id, name: (parent && parent.name) || part.name };
    };

    // Composants d'un groupe / forfait : produits + parts (forfaits).
    // NB : API en snake_case → `product_id`, `product_parts`, `product_part_id`.
    const componentsOf = (ref) => {
      if (!ref) return [];
      const comps = [];
      (ref.products || []).forEach((c) => {
        comps.push({ kind: "product", id: c.product_id, quantity: Number(c.quantity) || 1, name: c.name });
      });
      (ref.product_parts || []).forEach((c) => {
        comps.push({ kind: "part", id: c.product_part_id, quantity: Number(c.quantity) || 1, name: c.name });
      });
      return comps;
    };

    const componentUnit = (c) => (c.kind === "part" ? partPurchaseUnit(c.id) : productPurchase(c.id));
    const componentTarget = (c) =>
      c.kind === "part" ? partParentTarget(c.id) : (c.id ? { id: c.id, name: c.name } : null);

    // Coût dérivé d'un groupe/forfait à partir de ses composants
    const derivedOf = (ref) => {
      let total = 0;
      let hasAny = false;
      const miss = [];
      componentsOf(ref).forEach((c) => {
        const unit = componentUnit(c);
        if (unit !== null) {
          total += unit * c.quantity;
          hasAny = true;
        } else {
          const t = componentTarget(c);
          if (t) miss.push(t);
        }
      });
      return { total: Math.round(total), hasAny, missing: miss };
    };

    const refOfItem = (item) => {
      if (item.type === "group") return groupsById[item.product_group_id];
      if (item.type === "forfait") return packagesById[item.package_id];
      return null;
    };

    // Prix d'achat unitaire HT d'un item du devis (null si indéterminable)
    const unitPurchaseOf = (item) => {
      if (item.type === "product") return productPurchase(item.product_id);
      if (item.type === "part") return partPurchaseUnit(item.product_part_id);
      const ref = refOfItem(item);
      if (!ref) return null;
      const manual = ref.purchase_price_ht;
      if (manual !== null && manual !== undefined) return Number(manual);
      const d = derivedOf(ref);
      return d.hasAny ? d.total : null;
    };

    const familyOf = (item) => {
      if (item.type === "product") {
        const p = productsById[item.product_id];
        return (p && p.classificationName) || "Autres prestations";
      }
      if (item.type === "part") {
        const part = partsById[item.product_part_id];
        return (part && part.classificationName) || "Autres prestations";
      }
      if (item.type === "group") {
        const g = groupsById[item.product_group_id];
        return (g && (g.rangeName || g.range_name)) || "Groupes de produits";
      }
      if (item.type === "forfait") return "Forfaits";
      return "Autres prestations";
    };

    const map = {};
    const missingMap = {};
    const detailMap = {};
    const flagMissing = (m) => { missingMap[m.type + "-" + m.id] = m; };
    // Items entrant dans le calcul du coût d'achat (dédupliqués) → détail éditable
    const addDetail = (e) => {
      const k = e.type + "-" + e.id;
      if (!detailMap[k]) detailMap[k] = e;
    };

    (briquesDevis || []).forEach((brique) => {
      if (!brique || isDeliveryBrique(brique)) return;
      // Regroupement du récap par brique
      const briqueKey = (brique.name && String(brique.name).trim()) || "Brique sans nom";
      (brique.items || []).forEach((item) => {
        if (!item || item.type === "text") return;

        const isForfait = item.type === "forfait";
        const qty = isForfait
          ? Number(item.nb_persons) || 0
          : Number(item.quantity) || 1;
        const unitSale = isForfait
          ? Number(item.price_per_person) || 0
          : Number(item.unit_price) || 0;
        const lineSale = unitSale * qty;

        const unitP = unitPurchaseOf(item);
        const lineCostKnown = unitP !== null;
        const lineCost = lineCostKnown ? unitP * qty : 0;

        const family = familyOf(item); // conservé comme repli de nom d'item
        if (!map[briqueKey]) {
          map[briqueKey] = { family: briqueKey, venteHT: 0, achatHT: 0, incomplete: false };
        }
        map[briqueKey].venteHT += lineSale;
        map[briqueKey].achatHT += lineCost;

        if (lineSale <= 0) return;

        if (item.type === "product") {
          const pName = item.effective_name || item.name_override ||
            (productsById[item.product_id] && productsById[item.product_id].name) || family;
          addDetail({ type: "product", id: item.product_id, name: pName, unitPurchase: unitP });
          if (!lineCostKnown) {
            map[briqueKey].incomplete = true;
            flagMissing({ type: "product", id: item.product_id, name: pName });
          }
          return;
        }

        // Part de produit : l'item éditable du calcul est le produit PARENT
        // (coût part = prix d'achat parent / partsPerProduct).
        if (item.type === "part") {
          const part = partsById[item.product_part_id];
          const partName = item.effective_name || item.name_override || (part && part.name) || family;
          const target = partParentTarget(item.product_part_id);
          if (target) {
            addDetail({ type: "product", id: target.id, name: target.name, context: partName, unitPurchase: productPurchase(target.id) });
          }
          if (!lineCostKnown) {
            map[briqueKey].incomplete = true;
            if (target) flagMissing({ type: "product", id: target.id, name: target.name });
          }
          return;
        }

        // Groupe / forfait
        const ref = refOfItem(item);
        const manual = ref ? ref.purchase_price_ht : null;
        const offerId = item.type === "group" ? item.product_group_id : item.package_id;
        const offerName = item.effective_name || item.name_override || (ref && ref.name) || family;

        if (manual !== null && manual !== undefined) {
          // Prix saisi sur l'offre → c'est lui l'item du calcul
          addDetail({ type: item.type, id: offerId, name: offerName, unitPurchase: Number(manual) });
          return;
        }

        // Dérivé : les composants (produits + parts) sont les items du calcul.
        // Pour une part, l'item éditable est son produit parent.
        const comps = componentsOf(ref);
        comps.forEach((c) => {
          const unit = componentUnit(c);
          const target = componentTarget(c);
          if (target) {
            addDetail({ type: "product", id: target.id, name: target.name, context: offerName, unitPurchase: productPurchase(target.id) });
          }
          if (unit === null) {
            map[briqueKey].incomplete = true;
            if (target) flagMissing({ type: "product", id: target.id, name: target.name });
          }
        });
        // Aucun composant (ou pas de fiche) → proposer un prix global sur l'offre
        if (comps.length === 0) {
          map[briqueKey].incomplete = true;
          flagMissing({ type: item.type, id: offerId, name: offerName });
          addDetail({ type: item.type, id: offerId, name: offerName, unitPurchase: null });
        }
      });
    });

    const families = Object.values(map);
    const totalVente = families.reduce((s, f) => s + f.venteHT, 0);

    // Liste des réductions (multiples + typées), avec repli sur l'ancien
    // champ unique `reduction`.
    const reductionsList = Array.isArray(reductions)
      ? reductions
      : (reduction && (reduction.direct || reduction.proportional) ? [reduction] : []);
    let reductionAmount = 0;
    reductionsList.forEach((r) => {
      const direct = Number(r?.direct) || 0;
      const proportional = Number(r?.proportional) || 0;
      if (proportional > 0) reductionAmount += (totalVente * proportional) / 100;
      else if (direct > 0) reductionAmount += direct;
    });
    if (reductionAmount > totalVente) reductionAmount = totalVente;

    const applyReduction = (vente) =>
      totalVente > 0 ? vente - reductionAmount * (vente / totalVente) : vente;

    families.forEach((f) => {
      f.venteAvantRemise = Math.round(f.venteHT);
      f.margeAvantRemise = f.venteAvantRemise - f.achatHT;
      f.tauxAvantRemise = f.venteAvantRemise > 0 ? (f.margeAvantRemise / f.venteAvantRemise) * 100 : 0;
      f.venteApresRemise = Math.round(applyReduction(f.venteHT));
      f.margeHT = f.venteApresRemise - f.achatHT;
      f.taux = f.venteApresRemise > 0 ? (f.margeHT / f.venteApresRemise) * 100 : 0;
    });
    // Pas de tri : on conserve l'ordre des briques tel qu'affiché dans le devis.

    const totalVenteApres = families.reduce((s, f) => s + f.venteApresRemise, 0);
    const totalVenteAvant = families.reduce((s, f) => s + f.venteAvantRemise, 0);
    const totalAchat = families.reduce((s, f) => s + f.achatHT, 0);
    const totalMarge = totalVenteApres - totalAchat;
    const totalMargeAvant = totalVenteAvant - totalAchat;
    const totals = {
      achatHT: totalAchat,
      venteAvantRemise: totalVenteAvant,
      tauxAvantRemise: totalVenteAvant > 0 ? (totalMargeAvant / totalVenteAvant) * 100 : 0,
      venteApresRemise: totalVenteApres,
      margeHT: totalMarge,
      taux: totalVenteApres > 0 ? (totalMarge / totalVenteApres) * 100 : 0,
    };

    return {
      families,
      totals,
      missing: Object.values(missingMap),
      detail: Object.values(detailMap),
    };
  }, [briquesDevis, products, productGroups, productPackages, reduction, reductions]);

  if (!families.length) {
    return null;
  }

  const fmtPct = (v) => `${(Math.round(v * 10) / 10).toFixed(1)} %`;

  const openEdit = (m) => {
    setEditing(m);
    setDraft("");
  };

  const saveEdit = () => {
    if (!editing) return;
    const euros = parseFloat(String(draft).replace(",", "."));
    if (isNaN(euros)) return;
    const cents = Math.round(euros * 100);
    dispatch(updatePurchasePrice(editing.type, editing.id, cents));
    setEditing(null);
  };

  const typeLabel = (it) =>
    it.type === "product"
      ? (it.context ? "Composant" : "Produit")
      : it.type === "group"
      ? "Groupe"
      : "Forfait";

  // Édition inline d'une ligne du détail (au blur / Entrée)
  const commitDetail = (it) => {
    const key = it.type + "-" + it.id;
    const raw = detailDrafts[key];
    setDetailDrafts((d) => {
      const n = { ...d };
      delete n[key];
      return n;
    });
    if (raw === undefined || raw === "") return;
    const euros = parseFloat(String(raw).replace(",", "."));
    if (isNaN(euros)) return;
    dispatch(updatePurchasePrice(it.type, it.id, Math.round(euros * 100)));
  };

  return (
    <div
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        border: "1px solid #6f42c1",
        borderRadius: "10px",
        margin: "10px 0",
        padding: "15px",
      }}
    >
      <h5 style={{ marginBottom: "4px" }}>
        📊 Rentabilité (admin)
        <span
          className="badge"
          style={{
            marginLeft: "8px",
            backgroundColor: "#fff3cd",
            color: "#856404",
            border: "1px solid #ffeeba",
            fontWeight: 600,
            verticalAlign: "middle",
          }}
        >
          🚧 En cours de développement
        </span>
      </h5>
      <div
        style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffeeba",
          color: "#856404",
          borderRadius: "6px",
          padding: "6px 10px",
          marginBottom: "8px",
          fontSize: "0.85rem",
        }}
      >
        ⚠️ Cette fonctionnalité est en cours de développement — les montants sont indicatifs et peuvent évoluer.
      </div>
      <div className="text-muted small mb-2">
        Marges indicatives depuis les prix d'achat des fiches (groupes et forfaits : dérivé des produits composants si non renseigné). Hors livraison.
      </div>
      <Table size="sm" bordered hover responsive style={{ marginBottom: 0 }}>
        <thead>
          <tr>
            <th>Brique</th>
            <th className="text-right">Achat HT</th>
            <th className="text-right">Vente HT avant remise</th>
            <th className="text-right">Taux de marge avant remise</th>
            <th className="text-right">Vente HT après remise</th>
            <th className="text-right">Marge HT</th>
            <th className="text-right">Taux de marge</th>
          </tr>
        </thead>
        <tbody>
          {families.map((f) => (
            <tr key={f.family}>
              <td>
                {f.family}
                {f.incomplete && <span style={{ color: "#b8860b" }}> ⚠️</span>}
              </td>
              <td className="text-right">{centimesToCurrency(f.achatHT)}</td>
              <td className="text-right">{centimesToCurrency(f.venteAvantRemise)}</td>
              <td className="text-right" style={{ color: f.margeAvantRemise < 0 ? "#c0392b" : "inherit" }}>
                {fmtPct(f.tauxAvantRemise)}
              </td>
              <td className="text-right">{centimesToCurrency(f.venteApresRemise)}</td>
              <td className="text-right" style={{ color: f.margeHT < 0 ? "#c0392b" : "inherit" }}>
                {centimesToCurrency(f.margeHT)}
              </td>
              <td className="text-right" style={{ color: f.margeHT < 0 ? "#c0392b" : "inherit" }}>
                {fmtPct(f.taux)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600, borderTop: "2px solid #6f42c1" }}>
            <td>SOMME</td>
            <td className="text-right">{centimesToCurrency(totals.achatHT)}</td>
            <td className="text-right">{centimesToCurrency(totals.venteAvantRemise)}</td>
            <td className="text-right">{fmtPct(totals.tauxAvantRemise)}</td>
            <td className="text-right">{centimesToCurrency(totals.venteApresRemise)}</td>
            <td className="text-right" style={{ color: totals.margeHT < 0 ? "#c0392b" : "inherit" }}>
              {centimesToCurrency(totals.margeHT)}
            </td>
            <td className="text-right" style={{ color: totals.margeHT < 0 ? "#c0392b" : "inherit" }}>
              {fmtPct(totals.taux)}
            </td>
          </tr>
        </tfoot>
      </Table>

      {missing.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, color: "#b8860b", marginBottom: 6 }}>
            ⚠️ Prix d'achat manquants — clique pour le renseigner :
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {missing.map((m) => (
              <Button
                key={m.type + "-" + m.id}
                size="sm"
                variant="warning"
                onClick={() => openEdit(m)}
              >
                {m.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {detail.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setShowDetail((s) => !s)}
          >
            {showDetail ? "Masquer" : "Voir / modifier"} les prix d'achat du calcul ({detail.length})
          </Button>
          {showDetail && (
            <Table size="sm" bordered hover responsive style={{ marginTop: 8, marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th className="text-right" style={{ width: 150 }}>Prix d'achat HT</th>
                </tr>
              </thead>
              <tbody>
                {detail.map((it) => {
                  const key = it.type + "-" + it.id;
                  const val =
                    detailDrafts[key] !== undefined
                      ? detailDrafts[key]
                      : it.unitPurchase != null
                      ? (it.unitPurchase / 100).toFixed(2)
                      : "";
                  return (
                    <tr key={key}>
                      <td>
                        {it.name}
                        {it.context ? (
                          <span className="text-muted"> (dans {it.context})</span>
                        ) : null}
                      </td>
                      <td>{typeLabel(it)}</td>
                      <td className="text-right">
                        <InputGroup size="sm" style={{ maxWidth: 140, marginLeft: "auto" }}>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="—"
                            value={val}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDetailDrafts((d) => ({ ...d, [key]: v }));
                            }}
                            onBlur={() => commitDetail(it)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                          />
                          <InputGroup.Append>
                            <InputGroup.Text>€</InputGroup.Text>
                          </InputGroup.Append>
                        </InputGroup>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      )}

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Renseigner le prix d'achat HT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontWeight: 600 }}>{editing?.name}</p>
          <InputGroup>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              autoFocus
              value={draft}
              placeholder="0,00"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
              }}
            />
            <InputGroup.Append>
              <InputGroup.Text>€</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditing(null)}>
            Annuler
          </Button>
          <Button variant="success" onClick={saveEdit}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MarginSummaryPanel;
